import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, X } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { CURRENCY_ORDER } from '../../constants/currencies';

const invoiceTypes = [
  { value: 'all', label: 'Tümü' },
  { value: 'Sales', label: 'Gelir' },
  { value: 'Purchase', label: 'Gider' },
  { value: 'SalesReturn', label: 'Gelir İade' },
  { value: 'PurchaseReturn', label: 'Gider İade' }
];

const paymentStatuses = [
  { value: 'all', label: 'Tümü' },
  { value: 'Unpaid', label: 'Ödenmedi' },
  { value: 'PartiallyPaid', label: 'Kısmi Ödeme' },
  { value: 'Paid', label: 'Ödendi' }
];

const currencies = ['all', ...CURRENCY_ORDER];

const getRowTone = (type) => {
  if (type === 'Sales' || type === 'PurchaseReturn') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }
  return 'bg-rose-50 text-rose-700 border-rose-100';
};

const OperationInvoiceAttachModal = ({
  isOpen,
  operation,
  onClose,
  onAttachInvoice
}) => {
  const [loading, setLoading] = useState(false);
  const [attachingId, setAttachingId] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [currency, setCurrency] = useState('all');
  const [invoices, setInvoices] = useState([]);

  const accountId = operation?.account?._id || operation?.account || '';

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setType('all');
      setPaymentStatus('all');
      setCurrency('all');
      setInvoices([]);
      return undefined;
    }

    let ignore = false;

    const loadInvoices = async () => {
      setLoading(true);
      try {
        const params = {
          page: 1,
          limit: 100,
          operation: 'unassigned'
        };

        if (accountId) {
          params.accountId = accountId;
        }
        if (search.trim()) {
          params.search = search.trim();
        }
        if (type !== 'all') {
          params.type = type;
        }
        if (paymentStatus !== 'all') {
          params.paymentStatus = paymentStatus;
        }
        if (currency !== 'all') {
          params.currency = currency;
        }

        const response = await axios.get('/api/invoices', { params });
        if (ignore) return;

        const list = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        setInvoices(list.filter((invoice) => (
          invoice.status !== 'Cancelled' &&
          (invoice.operation === null || invoice.operation === undefined || invoice.operation === '')
        )));
      } catch {
        if (!ignore) setInvoices([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadInvoices();

    return () => {
      ignore = true;
    };
  }, [isOpen, accountId, search, type, paymentStatus, currency]);

  const filteredInvoices = useMemo(() => invoices, [invoices]);

  const handleClose = () => {
    onClose?.();
  };

  const handleAttach = async (invoice) => {
    if (!invoice?._id || typeof onAttachInvoice !== 'function') return;

    setAttachingId(invoice._id);
    try {
      const result = await onAttachInvoice(invoice);
      if (result?.success !== false) {
        handleClose();
      }
    } finally {
      setAttachingId('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-5xl overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-6 py-5">
          <div>
            <h3 className="text-xl font-black text-slate-800">Fatura Ekle</h3>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              İlişkisiz ve uygun faturaları seçin
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-900"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid gap-4 border-b border-slate-100 px-6 py-5 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Ara
            </label>
            <div className="relative mt-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Fatura no, not, cari adı..."
                className="w-full rounded-2xl border-none bg-slate-50 py-4 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
              />
            </div>
          </div>

          <div>
            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Tip
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
            >
              {invoiceTypes.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Para Birimi
            </label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
            >
              {currencies.map((option) => (
                <option key={option} value={option}>{option === 'all' ? 'Tümü' : option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="ml-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Ödeme Durumu
            </label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="mt-1 w-full rounded-2xl border-none bg-slate-50 px-4 py-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-slate-100"
            >
              {paymentStatuses.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-4 md:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-400">
              <Loader2 size={28} className="animate-spin text-blue-600" />
              <p className="text-[10px] font-black uppercase tracking-widest">Uygun faturalar aranıyor</p>
            </div>
          ) : filteredInvoices.length > 0 ? (
            <div className="overflow-hidden rounded-[1.5rem] border border-slate-100">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80">
                  <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <th className="p-4">Fatura</th>
                    <th className="p-4">Cari</th>
                    <th className="p-4">Tip</th>
                    <th className="p-4">Tarih</th>
                    <th className="p-4 text-right">Tutar</th>
                    <th className="p-4 text-right">Kalan</th>
                    <th className="p-4 text-right">Aksiyon</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 bg-white">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice._id} className="text-sm">
                      <td className="p-4">
                        <div className="font-black text-slate-800">{invoice.invoiceNumber || '-'}</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {invoice.paymentStatus || 'Unpaid'}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-bold text-slate-700">{invoice.accountId?.name || invoice.accountName || '-'}</div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {invoice.accountId?.code || 'Kodsuz'}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex rounded-lg border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${getRowTone(invoice.type)}`}>
                          {invoice.type}
                        </span>
                      </td>
                      <td className="p-4 font-bold text-slate-600">
                        {invoice.date ? new Date(invoice.date).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="p-4 text-right font-black text-slate-800">
                        {formatCurrency(invoice.grandTotal || 0, invoice.currency || 'TRY')}
                      </td>
                      <td className="p-4 text-right font-black text-slate-500">
                        {formatCurrency(invoice.remainingAmount || 0, invoice.currency || 'TRY')}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => handleAttach(invoice)}
                          disabled={attachingId === invoice._id}
                          className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 text-xs font-black text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {attachingId === invoice._id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            'Bağla'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50/70 px-6 py-16 text-center">
              <p className="text-sm font-bold text-slate-400">Uygun fatura bulunamadı.</p>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                Filtreleri değiştirerek tekrar deneyin.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationInvoiceAttachModal;
